import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import React from 'react';
import { Box, PolymorphicComponentProps } from 'react-polymorphic-box';
import Text from '../Typography/Text';
import UntitledLoading from '../UntitledLoading/UntitledLoading';

export interface MenuItemCoreBaseProps {
  disabled?: boolean;
  isProcessing?: boolean;
  isSelected?: boolean;
  disabledReasons?: string[];
  wrapperClassName?: string;
  iconName?: TIconName;
}

export type TProps<E extends React.ElementType> = PolymorphicComponentProps<
  E,
  MenuItemCoreBaseProps
>;

const defaultElement = 'button';

/**
 * A single interactive row for menus and menu-like lists: optional icon,
 * label, and processing/selected affordances.
 *
 * ## When to use
 *
 * - Rows inside dropdown and context menus.
 * - The row primitive under menu-like composites — `NavList` builds on it.
 *
 * ## When to choose another component
 *
 * - **Page or section navigation lists** — use `NavList`. It adds list
 *   semantics and collapsible groups on top of this row.
 * - **A standalone action** — use `Button`.
 *
 * ## State styling
 *
 * The row styles its states from attributes, not props:
 *
 * - `data-focus`, `data-active`, `data-disabled` — set any of these to apply
 *   the focus, active, or disabled treatment. Headless UI components set them
 *   automatically; set them yourself when composing manually.
 * - Native `:focus` and `:disabled` receive the same treatments, so plain
 *   elements need no data attributes.
 * - `aria-current="page"` also applies the active treatment — react-router's
 *   `NavLink` sets it on the active link, so `as={NavLink}` highlights the
 *   current page with no extra wiring.
 */
const MenuItemCore: <E extends React.ElementType = typeof defaultElement>(
  props: TProps<E>
) => React.ReactNode | null = React.forwardRef(
  <E extends React.ElementType = typeof defaultElement>(
    {
      children,
      wrapperClassName,
      iconName,
      className,
      isProcessing = false,
      isSelected = false,
      disabled,
      disabledReasons,
      ...rest
    }: TProps<E>,
    ref: typeof rest.ref
  ) => {
    return (
      // @ts-expect-error — react-polymorphic-box is not compatible with TypeScript v5
      <Box
        as={defaultElement}
        className={clstx(
          'flex w-full text-left cursor-pointer items-center gap-x-3.5 rounded-lg px-3 py-2 text-base bg-transparent font-normal text-blue-900 no-underline',
          // Hover - when not disabled
          'hover:not-data-disabled:bg-gray-100 hover:not-disabled:bg-gray-100',
          // Disabled
          'data-disabled:cursor-not-allowed data-disabled:opacity-50 disabled:cursor-not-allowed disabled:opacity-50',
          // Focus
          'data-focus:bg-gray-100 focus:bg-gray-100 focus:outline-none',
          // Active
          'data-active:bg-gray-100',
          // aria-current=page - the active state for <NavLink /> component from react-router
          'aria-[current=page]:bg-gray-100',
          wrapperClassName
        )}
        disabled={disabled}
        ref={ref}
        {...rest}
      >
        <div className="min-w-0 flex-1">
          <div className="flex gap-2.5 items-center">
            {iconName && <UntitledIcon name={iconName} className="size-5" />}

            {typeof children === 'string' ? (
              <span className={className}>
                <Text as="span">{children}</Text>
              </span>
            ) : (
              children
            )}
          </div>

          {disabled && disabledReasons && disabledReasons.length > 0 && (
            <>
              {disabledReasons.length === 1 ? (
                <div className="max-w-40">
                  <Text level="supporting" colour="primary">
                    {disabledReasons[0]}
                  </Text>
                </div>
              ) : (
                <Text as="div" level="supporting" colour="primary">
                  <ul className="max-w-40 list-disc pl-4">
                    {disabledReasons.map((reason, idx) => (
                      <li key={`${reason}-${idx}`}>{reason}</li>
                    ))}
                  </ul>
                </Text>
              )}
            </>
          )}
        </div>

        {isProcessing && (
          <UntitledLoading className="ml-auto h-4 w-4 shrink-0" />
        )}
        {isSelected && (
          <UntitledIcon
            name="check"
            className="ml-auto h-3.5 w-3.5 shrink-0 text-blue-600"
          />
        )}
      </Box>
    );
  }
);

export default MenuItemCore;
